import React, { useState, useEffect, useCallback } from 'react';
import { Select, Card, Typography, Spin, Alert, Row, Col, Button, message, Modal } from 'antd';
import { ClockCircleOutlined, BarChartOutlined, UserOutlined, PlusOutlined, InfoCircleOutlined, CrownOutlined } from '@ant-design/icons';
import { useSearchParams } from 'react-router-dom';
import { useScopedNavigate } from '../MarketerView/useScopedNavigate';
import { useIntl } from 'react-intl';
import BrandAnalyticsChart, { getAvailableMetrics, getDrillDownData } from './BrandAnalyticsChart';
import { CustomChartModal, CustomChartCard } from '../CustomCharts';
import {
  useGetAdminBrandAnalyticsQuery,
  useGetOrgBrandAnalyticsQuery,
  useGetCustomBrandChartsQuery,
  useDeleteCustomBrandChartMutation,
  useReorderCustomBrandChartsMutation,
  useCalculateBrandEquityMutation,
  IBrandAnalyticsMetricOption,
  ICustomBrandChart,
  IBrandEquityScoreResponse,
} from '../../services/nestjs-base-api';
import { useAuth } from '../../hooks/useAuth';
import { useElementAccess } from '../../hooks/useTierAccess';

const { Title } = Typography;
const { Option } = Select;

interface BrandAnalyticsComponentProps {
  brandHash: string;
  orgId?: string; // If provided, uses org endpoints; otherwise uses admin endpoints
  brandName?: string;
  /** Controlled period (`{N}_days` / `all_time`). When set, follows this instead
   *  of the component's own localStorage/URL state — used by the Marketer View so
   *  the headline Brand Equity matches the other tabs (SYR-68). */
  periodOverride?: string;
  /** Hide the in-component Time Period selector (the global scope drives it). */
  hidePeriodSelector?: boolean;
}

interface DrillDownState {
  level: 'latent' | 'measuredElement' | 'statements';
  parentMetric: string | null;
  availableMetrics: IBrandAnalyticsMetricOption[];
  previousSelectedMetrics?: string[]; // Store previous selection for back button
}

const BrandAnalyticsComponent: React.FC<BrandAnalyticsComponentProps> = ({
  brandHash,
  orgId,
  brandName: propBrandName,
  periodOverride,
  hidePeriodSelector = false,
}) => {
  const intl = useIntl();
  const navigate = useScopedNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const { user } = useAuth();

  // Element access for subscription tier restrictions
  const { isElementAllowed, hasUnrestrictedAccess } = useElementAccess();

  // Check if an element is locked (not allowed by subscription)
  const isElementLocked = useCallback((elementName: string) => {
    if (hasUnrestrictedAccess) return false;
    return !isElementAllowed(elementName, 'latent') && !isElementAllowed(elementName, 'questionCategory');
  }, [hasUnrestrictedAccess, isElementAllowed]);

  // State management with localStorage persistence
  const getStoredPeriod = () => {
    if (periodOverride) return periodOverride;
    const stored = localStorage.getItem(`brand-analytics-period-${brandHash}`);
    return stored || searchParams.get('period') || '180_days';
  };

  const [period, setPeriod] = useState(getStoredPeriod());

  // Follow the controlled period when the global scope changes (Marketer View).
  useEffect(() => {
    if (periodOverride && periodOverride !== period) {
      setPeriod(periodOverride);
    }
  }, [periodOverride]); // eslint-disable-line react-hooks/exhaustive-deps
  const [drillDownState, setDrillDownState] = useState<DrillDownState>({
    level: 'latent',
    parentMetric: null,
    availableMetrics: [],
    previousSelectedMetrics: []
  });

  // Custom charts state
  const [isChartModalVisible, setIsChartModalVisible] = useState(false);
  const [editingChart, setEditingChart] = useState<ICustomBrandChart | null>(null);
  const [newlyCreatedChartId, setNewlyCreatedChartId] = useState<number | null>(null);
  const chartRefs = React.useRef<{ [key: number]: HTMLDivElement | null }>({});

  // Data fetching - use appropriate hook based on whether orgId is provided
  const adminQuery = useGetAdminBrandAnalyticsQuery(
    { 
      jwtToken: user?.jwtToken || '', 
      brandHash, 
      period 
    },
    { skip: !!orgId || !user?.jwtToken }
  );

  const orgQuery = useGetOrgBrandAnalyticsQuery(
    { 
      jwtToken: user?.jwtToken || '', 
      orgId: orgId!, 
      brandHash, 
      period 
    },
    { skip: !orgId || !user?.jwtToken }
  );

  // Select the appropriate query result
  const query = orgId ? orgQuery : adminQuery;
  const { data: analyticsData, isLoading, error, refetch } = query;

  const brandName = propBrandName || analyticsData?.brandName || 'Unknown Brand';

  // Fetch custom charts for this brand
  const {
    data: customCharts,
    isLoading: chartsLoading,
  } = useGetCustomBrandChartsQuery(
    { jwtToken: user?.jwtToken || '', brandHash },
    { skip: !user?.jwtToken || !brandHash }
  );

  // Custom charts mutations
  const [deleteChart] = useDeleteCustomBrandChartMutation();
  const [reorderCharts] = useReorderCustomBrandChartsMutation();

  // SYR-59 — period-aggregate BE for the summary card. The chart below
  // still shows monthly buckets; the headline number must always reflect
  // the selected period so it reconciles with Dashboard + Benchmarking.
  const [calculateBrandEquity] = useCalculateBrandEquityMutation();
  const [periodBE, setPeriodBE] = useState<IBrandEquityScoreResponse | null>(null);
  const [previousPeriodBE, setPreviousPeriodBE] = useState<IBrandEquityScoreResponse | null>(null);
  const [periodBELoading, setPeriodBELoading] = useState(false);

  // Update available metrics when data changes
  useEffect(() => {
    if (analyticsData?.timeSeriesData) {
      const availableMetrics = getAvailableMetrics(analyticsData.timeSeriesData);
      setDrillDownState(prev => ({
        ...prev,
        availableMetrics
      }));
    }
  }, [analyticsData]);

  // Update URL when period changes (skip when the period is externally
  // controlled — the global Marketer View scope owns it, not the URL).
  useEffect(() => {
    if (hidePeriodSelector) return;
    const newParams = new URLSearchParams(searchParams);
    newParams.set('period', period);
    setSearchParams(newParams);
  }, [period, searchParams, setSearchParams, hidePeriodSelector]);

  // SYR-59 — fetch period-aggregate BE (current + previous N days) whenever
  // period changes. Same calc the Dashboard and Benchmarking use.
  const PERIOD_DAYS: Record<string, number> = {
    '30_days': 30,
    '60_days': 60,
    '90_days': 90,
    '120_days': 120,
    '180_days': 180,
    '365_days': 365,
  };
  const orgIdForCalc = orgId || user?.organisation?.id?.toString();
  const brandIdFromAnalytics = analyticsData?.brandId;
  useEffect(() => {
    if (!user?.jwtToken || !orgIdForCalc || !brandIdFromAnalytics) return;
    const brandIdNum = parseInt(brandIdFromAnalytics, 10);
    if (Number.isNaN(brandIdNum)) return;
    let cancelled = false;
    const run = async () => {
      setPeriodBELoading(true);
      setPeriodBE(null);
      setPreviousPeriodBE(null);
      try {
        const currentResult = await calculateBrandEquity({
          jwtToken: user.jwtToken,
          organisationId: parseInt(orgIdForCalc, 10),
          payload: {
            brandIds: [brandIdNum],
            period: period as any,
            skipAiAnalysis: true,
          },
        }).unwrap();
        if (cancelled) return;
        setPeriodBE(currentResult);

        const days = PERIOD_DAYS[period];
        if (days) {
          const now = new Date();
          const prevTo = new Date(now.getTime() - days * 24 * 60 * 60 * 1000);
          const prevFrom = new Date(prevTo.getTime() - days * 24 * 60 * 60 * 1000);
          const prevResult = await calculateBrandEquity({
            jwtToken: user.jwtToken,
            organisationId: parseInt(orgIdForCalc, 10),
            payload: {
              brandIds: [brandIdNum],
              period: 'date_range' as any,
              dateFrom: prevFrom.toISOString(),
              dateTo: prevTo.toISOString(),
              skipAiAnalysis: true,
            },
          }).unwrap();
          if (!cancelled) setPreviousPeriodBE(prevResult);
        }
      } catch (err) {
        console.error('Period-aggregate BE calc failed:', err);
      } finally {
        if (!cancelled) setPeriodBELoading(false);
      }
    };
    run();
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [period, brandIdFromAnalytics, orgIdForCalc, user?.jwtToken]);

  const handlePeriodChange = (newPeriod: string) => {
    setPeriod(newPeriod);
    // Save period preference to localStorage
    localStorage.setItem(`brand-analytics-period-${brandHash}`, newPeriod);
    // Reset drill-down when period changes
    setDrillDownState(prev => ({
      ...prev,
      level: 'latent',
      parentMetric: null
    }));
  };

  const handleLineClick = (metric: string, level: string) => {
    if (!analyticsData?.timeSeriesData) return;

    // Determine next drill-down level
    let nextLevel: 'latent' | 'measuredElement' | 'statements' = 'measuredElement';
    
    if (level === 'measuredElement') {
      nextLevel = 'statements';
    }

    // Get available metrics for the next level
    const drillDownOptions = getDrillDownData(metric, analyticsData.timeSeriesData, nextLevel);
    
    if (drillDownOptions.length === 0) {
      // No drill-down available
      return;
    }

    // Create metrics for the drill-down level
    const drillDownMetrics: IBrandAnalyticsMetricOption[] = drillDownOptions.map(option => ({
      key: option,
      label: option,
      type: nextLevel === 'statements' ? 'brandEquity' : 'latentElement',
      drillDownAvailable: nextLevel !== 'statements'
    }));

    setDrillDownState({
      level: nextLevel,
      parentMetric: metric,
      availableMetrics: drillDownMetrics
    });
  };

  // Handle drill-down from line click
  const handleDrillDown = (parentMetric: string, constituents: string[], currentSelectedMetrics: string[]) => {
    if (!analyticsData?.timeSeriesData) {
      return;
    }

    // Filter out locked constituent elements for restricted users
    const unlockedConstituents = constituents.filter(element => !isElementLocked(element));

    // If all constituents are locked, show a message instead of drilling down
    if (unlockedConstituents.length === 0 && constituents.length > 0) {
      message.info(intl.formatMessage({ id: 'subscription.upgrade.allConstituentsRequireUpgrade' }));
      return;
    }

    // Create metrics for the input elements (QC type) - only unlocked ones
    const drillDownMetrics: IBrandAnalyticsMetricOption[] = unlockedConstituents.map(inputElement => ({
      key: inputElement,
      label: inputElement,
      type: 'latentElement', // Use existing type for input elements
      drillDownAvailable: false // Input elements are the deepest level we show
    }));

    // Show info if some constituents were filtered out
    const lockedCount = constituents.length - unlockedConstituents.length;
    if (lockedCount > 0) {
      message.info(intl.formatMessage({ id: 'subscription.upgrade.elementsHidden' }, { count: lockedCount }));
    }

    setDrillDownState({
      level: 'measuredElement',
      parentMetric: parentMetric,
      availableMetrics: drillDownMetrics,
      previousSelectedMetrics: currentSelectedMetrics // Store current selection
    });
  };

  const handleBackClick = () => {
    if (drillDownState.level === 'statements') {
      // Go back to measuredElement level
      setDrillDownState(prev => ({
        level: 'measuredElement',
        parentMetric: prev.parentMetric,
        availableMetrics: [], // Will be recalculated
        previousSelectedMetrics: prev.previousSelectedMetrics
      }));
    } else {
      // Go back to top level
      const availableMetrics = analyticsData?.timeSeriesData
        ? getAvailableMetrics(analyticsData.timeSeriesData)
        : [];
      setDrillDownState({
        level: 'latent',
        parentMetric: null,
        availableMetrics,
        previousSelectedMetrics: drillDownState.previousSelectedMetrics // Keep this for the chart to restore
      });

      // Clear the previousSelectedMetrics after a short delay to allow the chart to restore
      setTimeout(() => {
        setDrillDownState(prev => ({
          ...prev,
          previousSelectedMetrics: undefined
        }));
      }, 100);
    }
  };

  // Custom chart limit based on subscription tier
  const customChartLimit = hasUnrestrictedAccess ? 5 : 1;

  // Custom chart handlers
  const handleAddChart = () => {
    if (!customCharts || customCharts.length < customChartLimit) {
      setEditingChart(null);
      setIsChartModalVisible(true);
    } else if (!hasUnrestrictedAccess) {
      // User at their 1 chart limit - show upgrade modal
      Modal.confirm({
        title: intl.formatMessage({ id: 'subscription.upgrade.customChartLimit.title' }),
        icon: <CrownOutlined style={{ color: '#F59E0B' }} />,
        content: intl.formatMessage({ id: 'subscription.upgrade.customChartLimit.content' }),
        okText: intl.formatMessage({ id: 'subscription.upgrade.button' }),
        cancelText: intl.formatMessage({ id: 'subscription.upgrade.maybeLater' }),
        onOk() {
          navigate('/dashboard/organisation?tab=billing&showUpgrade=true');
        },
      });
    } else {
      message.warning(
        intl.formatMessage({ id: 'customCharts.limit.reached.description' })
      );
    }
  };

  const handleEditChart = (chart: ICustomBrandChart) => {
    setEditingChart(chart);
    setIsChartModalVisible(true);
  };

  const handleDeleteChart = async (chartId: number) => {
    if (!user?.jwtToken || !customCharts) return;

    const chart = customCharts.find((c) => c.id === chartId);
    if (!chart) return;

    try {
      await deleteChart({
        jwtToken: user.jwtToken,
        id: chartId,
        brandHash,
      }).unwrap();
      message.success(intl.formatMessage({ id: 'customCharts.delete.success' }));
    } catch (error) {
      console.error('Failed to delete custom chart:', error);
      message.error(intl.formatMessage({ id: 'customCharts.delete.error' }));
    }
  };

  const handleMoveUp = async (chartId: number) => {
    if (!customCharts || !user?.jwtToken) return;

    const currentIndex = customCharts.findIndex((c) => c.id === chartId);
    if (currentIndex <= 0) return;

    const reordered = [...customCharts];
    [reordered[currentIndex - 1], reordered[currentIndex]] = [
      reordered[currentIndex],
      reordered[currentIndex - 1],
    ];

    try {
      await reorderCharts({
        jwtToken: user.jwtToken,
        data: {
          chartIds: reordered.map((c) => c.id),
          brandHash,
        },
      }).unwrap();
      message.success(intl.formatMessage({ id: 'customCharts.reorder.success' }));
    } catch (error) {
      console.error('Failed to reorder charts:', error);
      message.error(intl.formatMessage({ id: 'customCharts.reorder.error' }));
    }
  };

  const handleMoveDown = async (chartId: number) => {
    if (!customCharts || !user?.jwtToken) return;

    const currentIndex = customCharts.findIndex((c) => c.id === chartId);
    if (currentIndex < 0 || currentIndex >= customCharts.length - 1) return;

    const reordered = [...customCharts];
    [reordered[currentIndex], reordered[currentIndex + 1]] = [
      reordered[currentIndex + 1],
      reordered[currentIndex],
    ];

    try {
      await reorderCharts({
        jwtToken: user.jwtToken,
        data: {
          chartIds: reordered.map((c) => c.id),
          brandHash,
        },
      }).unwrap();
      message.success(intl.formatMessage({ id: 'customCharts.reorder.success' }));
    } catch (error) {
      console.error('Failed to reorder charts:', error);
      message.error(intl.formatMessage({ id: 'customCharts.reorder.error' }));
    }
  };

  const handleChartModalClose = () => {
    setIsChartModalVisible(false);
    setEditingChart(null);
  };

  const handleChartModalSuccess = (chartId?: number) => {
    if (chartId) {
      setNewlyCreatedChartId(chartId);
    }
  };

  // Scroll to newly created chart
  useEffect(() => {
    if (newlyCreatedChartId && chartRefs.current[newlyCreatedChartId]) {
      // Wait a bit for the chart to render
      setTimeout(() => {
        const element = chartRefs.current[newlyCreatedChartId];
        if (element) {
          element.scrollIntoView({ behavior: 'smooth', block: 'center' });
          setNewlyCreatedChartId(null); // Clear after scrolling
        }
      }, 100);
    }
  }, [newlyCreatedChartId, customCharts]);

  const periodLabel = ((): string => {
    switch (period) {
      case '30_days': return 'Last 30 Days';
      case '60_days': return 'Last 60 Days';
      case '90_days': return 'Last 90 Days';
      case '120_days': return 'Last 120 Days';
      case '180_days': return 'Last 180 Days';
      case '365_days': return 'Last 365 Days';
      case 'all_time': return 'All Time';
      default: return 'Selected Period';
    }
  })();

  const renderSummaryStats = () => {
    if (!analyticsData?.timeSeriesData) return null;

    const score = periodBE?.overallBrandEquityScore;
    const previousScore = previousPeriodBE?.overallBrandEquityScore;
    const change = score != null && previousScore != null ? score - previousScore : null;
    const totalResponses = periodBE?.totalResponses ?? 0;
    const periodSuffix = periodLabel.toLowerCase().replace(/^last /, '');

    return (
      <Row gutter={[24, 16]} style={{ marginBottom: '32px' }}>
        <Col xs={24} md={8}>
          <Card style={{
            borderRadius: '12px',
            border: '1px solid #e5e7eb',
            boxShadow: '0 2px 8px rgba(0,0,0,0.06)',
            height: '160px'
          }}>
            <div style={{ textAlign: 'center', padding: '20px 16px' }}>
              <div style={{
                fontSize: '40px',
                fontWeight: 700,
                color: '#2563eb',
                marginBottom: '12px',
                lineHeight: 1
              }}>
                {periodBELoading
                  ? <Spin size="small" />
                  : score != null
                    ? score.toFixed(1)
                    : '—'}
              </div>
              <div style={{
                fontSize: '15px',
                fontWeight: 600,
                color: '#374151',
                marginBottom: '8px'
              }}>
                Brand Equity Score ({periodLabel})
              </div>
              {change != null && Math.abs(change) >= 0.05 ? (
                <div style={{
                  fontSize: '13px',
                  color: change > 0 ? '#059669' : '#dc2626',
                  fontWeight: 500
                }}>
                  {change > 0 ? '↑' : '↓'} {Math.abs(change).toFixed(1)} vs previous {periodSuffix}
                </div>
              ) : null}
            </div>
          </Card>
        </Col>

        <Col xs={24} md={8}>
          <Card style={{
            borderRadius: '12px',
            border: '1px solid #e5e7eb',
            boxShadow: '0 2px 8px rgba(0,0,0,0.06)',
            height: '160px'
          }}>
            <div style={{ textAlign: 'center', padding: '20px 16px' }}>
              <div style={{
                fontSize: '40px',
                fontWeight: 700,
                color: '#059669',
                marginBottom: '12px',
                lineHeight: 1
              }}>
                {periodBELoading ? <Spin size="small" /> : totalResponses}
              </div>
              <div style={{
                fontSize: '15px',
                fontWeight: 600,
                color: '#374151',
                marginBottom: '8px'
              }}>
                Total Responses
              </div>
              <div style={{
                fontSize: '13px',
                color: '#6b7280',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                gap: '6px'
              }}>
                <UserOutlined /> {periodLabel}
              </div>
            </div>
          </Card>
        </Col>
        
        <Col xs={24} md={8}>
          <Card style={{
            borderRadius: '12px',
            border: '1px solid #e5e7eb',
            boxShadow: '0 2px 8px rgba(0,0,0,0.06)',
            height: '160px'
          }}>
            <div style={{ textAlign: 'center', padding: '20px 16px' }}>
              <div style={{ 
                fontSize: '28px', 
                fontWeight: 700, 
                color: '#7c3aed',
                marginBottom: '12px',
                lineHeight: 1
              }}>
                {analyticsData.industry}
              </div>
              <div style={{ 
                fontSize: '15px', 
                fontWeight: 600,
                color: '#374151',
                marginBottom: '8px'
              }}>
                Industry
              </div>
              <div style={{ 
                fontSize: '13px',
                color: '#6b7280',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                gap: '6px'
              }}>
                <BarChartOutlined /> Sector classification
              </div>
            </div>
          </Card>
        </Col>
      </Row>
    );
  };

  if (isLoading) {
    return (
      <div className="brand-analytics-container">
        <div className="brand-analytics-loading">
          <Spin size="large" />
          <div>Loading brand analytics data...</div>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="brand-analytics-container">
        <Alert
          message="Error Loading Analytics"
          description="Unable to load brand analytics data. Please try again later."
          type="error"
          action={
            <button onClick={() => refetch()}>Retry</button>
          }
        />
      </div>
    );
  }

  if (!analyticsData) {
    return (
      <div className="brand-analytics-container">
        <div className="brand-analytics-empty">
          <Title level={4}>No Data Available</Title>
          <p>No analytics data found for this brand.</p>
        </div>
      </div>
    );
  }

  return (
    <div style={{ padding: '20px 32px', maxWidth: '100%', width: '100%' }}>
      {/* Controls */}
      <div style={{ 
        display: 'flex', 
        gap: '16px', 
        marginBottom: '24px',
        alignItems: 'center'
      }}>
        {!hidePeriodSelector && (
          <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
            <label style={{
              fontSize: '14px',
              fontWeight: 500,
              color: '#374151',
              display: 'flex',
              alignItems: 'center',
              gap: '8px'
            }}>
              <ClockCircleOutlined /> Time Period
            </label>
            <Select
              value={period}
              onChange={handlePeriodChange}
              style={{
                minWidth: '180px',
                height: '40px'
              }}
              size="large"
            >
              <Option value="90_days">Last 90 Days</Option>
              <Option value="180_days">Last 180 Days</Option>
              <Option value="365_days">Last 365 Days</Option>
            </Select>
          </div>
        )}
      </div>

      {/* Custom Charts Info Banner - show at top when no charts exist */}
      {!chartsLoading && (!customCharts || customCharts.length === 0) && (
        <Card
          style={{
            background: 'linear-gradient(135deg, #F59E0B 0%, #FBBF24 100%)',
            border: 'none',
            marginBottom: 24,
          }}
        >
          <div style={{ display: 'flex', alignItems: 'center', gap: '16px' }}>
            <InfoCircleOutlined style={{ fontSize: 32, color: 'white' }} />
            <div style={{ flex: 1 }}>
              <Typography.Title level={4} style={{ margin: 0, color: 'white' }}>
                Create Your First Custom Chart
              </Typography.Title>
              <Typography.Text style={{ color: 'rgba(255, 255, 255, 0.95)', fontSize: 14 }}>
                Track specific metrics that matter most to you. Select up to 6 metrics and save them as a custom chart for quick access.
              </Typography.Text>
            </div>
            <Button
              type="default"
              size="large"
              onClick={handleAddChart}
              style={{
                background: 'white',
                borderColor: 'white',
                color: '#F59E0B',
                fontWeight: 600,
              }}
            >
              Get Started
            </Button>
          </div>
        </Card>
      )}

      {/* Summary Statistics */}
      {renderSummaryStats()}

      {/* Main Chart */}
      <BrandAnalyticsChart
        data={analyticsData.timeSeriesData}
        availableMetrics={drillDownState.availableMetrics}
        brandName={brandName}
        brandHash={brandHash}
        onLineClick={handleLineClick}
        drillDownLevel={drillDownState.level}
        parentMetric={drillDownState.parentMetric}
        onBackClick={handleBackClick}
        isLoading={isLoading}
        onDrillDown={handleDrillDown}
        previousSelectedMetrics={drillDownState.previousSelectedMetrics}
        isElementLocked={isElementLocked}
        hasUnrestrictedAccess={hasUnrestrictedAccess}
        minResponseThreshold={analyticsData.minResponseThreshold}
        lowConfidenceThreshold={analyticsData.lowConfidenceThreshold}
      />

      {/* Additional insights could go here */}
      {drillDownState.parentMetric && (
        <Card
          title={`${drillDownState.parentMetric} Insights`}
          style={{ marginTop: 16 }}
        >
          <p>
            Detailed analysis of {drillDownState.parentMetric} showing
            {drillDownState.level === 'measuredElement' ? ' contributing measured elements.' : ' individual statement performance.'}
          </p>
        </Card>
      )}

      {/* Custom Charts Section */}
      <div style={{ marginTop: 48 }}>
        <div style={{
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'flex-start',
          gap: '8px',
          marginBottom: 24
        }}>
          <Button
            type="primary"
            icon={<PlusOutlined style={{ marginRight: 4 }} />}
            onClick={handleAddChart}
            size="large"
          >
            {intl.formatMessage({ id: 'customCharts.addChart' })}
          </Button>
          <Typography.Text type="secondary" style={{ fontSize: 13 }}>
            {intl.formatMessage({ id: 'customCharts.addChartDescription' })}
          </Typography.Text>
          {customCharts && customCharts.length > 0 && (
            <Typography.Text type="secondary" style={{ fontSize: 12 }}>
              {intl.formatMessage(
                { id: 'customCharts.limit.info' },
                { current: customCharts.length, max: customChartLimit }
              )}
            </Typography.Text>
          )}
        </div>

        {chartsLoading ? (
          <div style={{ textAlign: 'center', padding: 40 }}>
            <Spin />
            <div style={{ marginTop: 16 }}>
              {intl.formatMessage({ id: 'customCharts.loading' })}
            </div>
          </div>
        ) : (
          customCharts && customCharts.length > 0 && customCharts.map((chart, index) => (
            <div
              key={chart.id}
              ref={(el) => {
                chartRefs.current[chart.id] = el;
              }}
            >
              <CustomChartCard
                chart={chart}
                data={analyticsData?.timeSeriesData || []}
                availableMetrics={drillDownState.availableMetrics}
                brandName={brandName}
                brandHash={brandHash}
                isFirst={index === 0}
                isLast={index === customCharts.length - 1}
                onEdit={handleEditChart}
                onDelete={handleDeleteChart}
                onMoveUp={handleMoveUp}
                onMoveDown={handleMoveDown}
                isElementLocked={isElementLocked}
                hasUnrestrictedAccess={hasUnrestrictedAccess}
                minResponseThreshold={analyticsData?.minResponseThreshold}
                lowConfidenceThreshold={analyticsData?.lowConfidenceThreshold}
              />
            </div>
          ))
        )}
      </div>

      {/* Custom Chart Modal */}
      <CustomChartModal
        visible={isChartModalVisible}
        chart={editingChart}
        brandHash={brandHash}
        onClose={handleChartModalClose}
        onSuccess={handleChartModalSuccess}
      />
    </div>
  );
};

export default BrandAnalyticsComponent;